import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { formatEUR, SHIPPING_COST, FREE_SHIPPING_LIMIT } from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total } = useCart();
  const shipping = total >= FREE_SHIPPING_LIMIT || total === 0 ? 0 : SHIPPING_COST;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md bg-[#FBF9F5] flex flex-col p-0">
        <SheetHeader className="px-6 py-5 border-b border-[#E4E0D7]">
          <SheetTitle className="font-display text-xl">Vaša košarica</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-16" data-testid="cart-empty">
              <p className="text-stone-500">Vaša košarica je prazna.</p>
              <button
                data-testid="cart-empty-browse-btn"
                onClick={() => setOpen(false)}
                className="text-sm font-semibold text-[#B45309] hover:underline"
              >
                Nadaljujte z nakupom
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => {
                const key = item.key || item.id;
                return (
                  <li key={key} data-testid={`cart-item-${key}`} className="flex gap-3 bg-white rounded-xl border border-[#E4E0D7] p-3">
                    {item.image && (
                      <img src={item.image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#1A1D1A] leading-snug line-clamp-2">{item.name}</p>
                      {item.meta && <p className="text-[11px] text-stone-500 mt-0.5">{item.meta}</p>}
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-1 bg-[#F3EFE6] rounded-full px-1">
                          <button
                            data-testid={`cart-qty-minus-${key}`}
                            onClick={() => updateQty(key, item.qty - 1)}
                            className="w-7 h-7 flex items-center justify-center text-stone-600 hover:text-[#B45309]"
                          >
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span data-testid={`cart-qty-${key}`} className="w-6 text-center text-sm font-tech font-semibold">{item.qty}</span>
                          <button
                            data-testid={`cart-qty-plus-${key}`}
                            onClick={() => updateQty(key, item.qty + 1)}
                            className="w-7 h-7 flex items-center justify-center text-stone-600 hover:text-[#B45309]"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <span className="font-tech font-bold text-sm">{formatEUR(item.price * item.qty)}</span>
                      </div>
                    </div>
                    <button
                      data-testid={`cart-remove-${key}`}
                      onClick={() => removeItem(key)}
                      className="self-start p-1 text-stone-400 hover:text-red-600 transition-colors"
                      aria-label="Odstrani"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E4E0D7] px-6 py-5 space-y-3 bg-white">
            <div className="flex justify-between text-sm text-stone-600">
              <span>Vmesna vsota</span>
              <span data-testid="cart-subtotal" className="font-tech">{formatEUR(total)}</span>
            </div>
            <div className="flex justify-between text-sm text-stone-600">
              <span>Dostava</span>
              <span data-testid="cart-shipping" className="font-tech">
                {shipping === 0 ? "Brezplačno" : formatEUR(shipping)}
              </span>
            </div>
            <div className="flex justify-between font-display font-bold text-lg text-[#1A1D1A] pt-2 border-t border-[#E4E0D7]">
              <span>Skupaj z DDV</span>
              <span data-testid="cart-total" className="font-tech">{formatEUR(total + shipping)}</span>
            </div>
            <Link
              to="/blagajna"
              data-testid="go-to-checkout-btn"
              onClick={() => setOpen(false)}
              className="w-full h-12 rounded-full bg-[#B45309] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#92400e] transition-colors"
            >
              Na blagajno <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
